import React, { useEffect, useState } from "react";
import { CaretDownOutlined } from "@ant-design/icons";
import { DevCycleDebugger } from "@devcycle/web-debugger/react";
import { Dropdown } from "antd5";
import classnames from "classnames";
import SwitchCompanyModal from "pages/admin/SwitchCompanyModal";

import { DeploymentEnvironment, getDeploymentEnvironment, getSandboxName } from "lib/config";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { AdminAPIProvider } from "lib/stotlesAdminApiContext";

import css from "./AdminBar.module.scss";

const HIDE_ADMIN_BAR_KEY = "hideAdminBar";

const ENVIRONMENT_CLASSES: Record<DeploymentEnvironment, { bar: string; button: string }> = {
  development: { bar: css.adminDev, button: css.adminButtonDev },
  sandbox: { bar: css.adminSandbox, button: css.adminButtonSandbox },
  production: { bar: css.adminProd, button: css.adminButtonProd },
};

type Props = {
  content?: () => React.ReactNode;
};

type ModalShowed = null | "switch_company" | "switch_team";

export function AdminBar({ content }: Props) {
  const [show, setShow] = useState<boolean>(false);
  const [modalShowed, setModalShowed] = useState<ModalShowed>(null);
  const { data: currentUser } = useCurrentUser();

  const deploymentEnvironment = getDeploymentEnvironment();
  const { bar: cssAdminBarEnv, button: cssAdminButtonEnv } =
    ENVIRONMENT_CLASSES[deploymentEnvironment];

  const toggleAdminFeaturesVisability = (flag = true) => {
    sessionStorage.setItem(HIDE_ADMIN_BAR_KEY, String(flag));
    setShow(!flag);
  };

  useEffect(() => {
    if (sessionStorage.getItem(HIDE_ADMIN_BAR_KEY) !== "true") {
      toggleAdminFeaturesVisability(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (typeof event?.getModifierState !== "function") {
        return;
      }

      if (
        (event.getModifierState("Control") &&
          event.getModifierState("Shift") &&
          event.code === "KeyU") ||
        event.code === "KeyK"
      ) {
        toggleAdminFeaturesVisability(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (!currentUser || currentUser?.isAdmin === false) {
    return null;
  }

  let buttonLinkText = "DEVELOPMENT ADMIN";
  if (deploymentEnvironment === "sandbox") {
    buttonLinkText = `SANDBOX ADMIN: ${getSandboxName()}`;
  } else if (deploymentEnvironment === "production") {
    buttonLinkText = "ADMIN";
  }

  const menuItems = [
    {
      key: "switch_company",
      onClick: () => setModalShowed("switch_company"),
      label: "Switch company",
    },
    {
      key: "switch_team",
      onClick: () => setModalShowed("switch_team"),
      label: "Switch team",
    },
  ];

  return (
    <section className={classnames(css.adminBar, cssAdminBarEnv, show ? css.show : null)}>
      {/* eslint-disable-next-line no-restricted-syntax -- admin is a separate pack with no router */}
      <a href="/admin" className={classnames(cssAdminButtonEnv, css.adminButton)}>
        {buttonLinkText}
      </a>
      <AdminAPIProvider>
        <SwitchCompanyModal
          userGuid={currentUser.guid}
          onClose={() => setModalShowed(null)}
          isOpen={modalShowed !== null}
          switchTeams={modalShowed === "switch_team"}
        />
      </AdminAPIProvider>
      <div className={css.adminBarContent}>{content?.()}</div>
      <div className={css.adminBarProfile}>
        <Dropdown menu={{ items: menuItems }}>
          <a href={`/admin/users/${currentUser.id}`} target="_blank" rel="noreferrer">
            {currentUser.email}
            <CaretDownOutlined />
          </a>
        </Dropdown>
        <a href={`/admin/companies/${currentUser.company.id}`} target="_blank" rel="noreferrer">
          {currentUser.company.name}
          {currentUser.team.name}
        </a>
        <button
          onClick={() => toggleAdminFeaturesVisability()}
          className={css.adminBarClose}
          title="To show the admin bar again press Ctrl+Shift+U (U as in unhide)."
        >
          &#10799;
        </button>
      </div>
      {show ? <DevCycleDebugger position="left" shouldEnable={show} /> : null}
    </section>
  );
}
